/* eslint no-plusplus: 0 */
import { Color, Point2, Point3, Matrix, FaceWinding, MeshData } from "@ts3d-hoops/web-viewer";
import type { IWebViewer, Markup, NodeId, Plane } from "@ts3d-hoops/web-viewer";
import { store } from "@Store";
import type { EstimateData } from "@ServerTypes/estimates/estimates.schema.js";

declare global {
  interface Window {
    // Deliberately left `any`. `CubeGlobal` is an open registry mutated from 43 files outside
    // this module (viewer-toolbar, viewer-modals, design-viewer, estimation, issues, services,
    // …) under 79 distinct property names, and every custom markup/operator class is hung off
    // it at import time. A real interface here would have to declare all of them and would
    // break modules this refactor may not edit. See the local types below for the members this
    // file does describe.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ambient CubeGlobal published by a third-party script tag
    CubeGlobal: any;
  }
}

// Initialize and export a single shared CubeGlobal object for the app
export const CubeGlobal = (() => {
  if (typeof window !== "undefined") {
    window.CubeGlobal = window.CubeGlobal || {};
    return window.CubeGlobal;
  }
  return {};
})();

// The custom-operator registry. Its *contents* are runtime handles that cannot exist until
// `ViewerImpl.registerOperators` has run against a live viewer, but the container depends on
// nothing — so it is created here alongside CubeGlobal rather than inside `InitializeUtils`,
// which runs from an effect gated on file + settings data. BottomToolbar and QuantityTakeoffToolbar
// read `CubeGlobal.OperatorId.<name>` in their render bodies, which happens on the first commit,
// long before that effect can fire (Sentry CUBE-FRONTEND-7ZF).
CubeGlobal.OperatorId = {};

/** Bidirectional unit lookup: `uType.m` gives the index, `uType[index]` gives the symbol.
 *  The named keys are optional only because the literal starts empty; the block below sets
 *  every one of them before the object escapes. */
type LinearUnitLookup = string[] & { mm?: number; cm?: number; m?: number; inch?: number; feet?: number };

type AreaUnitLookup = string[] & {
  sqmm?: number;
  sqcm?: number;
  sqm?: number;
  sqinch?: number;
  sqfeet?: number;
  acre?: number;
  hectare?: number;
};

type VolumeUnitLookup = string[] & {
  cubmm?: number;
  cubcm?: number;
  cubm?: number;
  cubinch?: number;
  cubfeet?: number;
};

/** The subset of `Markup.Shapes.TextBox` this file restyles. `_text` and `_box` are private on
 *  the HOOPS class but are reached here to apply portal styling after construction, so they are
 *  named structurally rather than through the class type. */
interface StyleableTextBox {
  setPadding(padding: number): void;
  _text?: Markup.Shapes.Text;
  _box?: Markup.Shapes.Rectangle;
}

/** The markup shapes `ApplyMeasurementStyling` restyles. Every member is optional because the
 *  custom markup classes registered on `CubeGlobal` each carry a different subset. */
interface StyleableMarkup {
  _textShape?: StyleableTextBox;
  _texts?: Markup.Shapes.TextCollection;
  _textBoxes?: StyleableTextBox;
}

CubeGlobal.FlatShading = 10;
CubeGlobal.socketDisconnected = false;

export const DISCONNECT_TOAST_MIN_INTERVAL_MS = 10_000;

CubeGlobal.disconnectToastLastShownAt = 0;

export function resetDisconnectToastThrottle() {
  CubeGlobal.disconnectToastLastShownAt = 0;
}

export const InitializeUtils = function () {
  CubeGlobal.pointTolerance = 0.0001;

  // Accessing redux store to get unit details for estimate

  // Unit types required for length conversion between one type to another
  const uType: LinearUnitLookup = [];
  uType[(uType.mm = 0)] = "mm";
  uType[(uType.cm = 1)] = "cm";
  uType[(uType.m = 2)] = "m";
  uType[(uType.inch = 3)] = "inch";
  uType[(uType.feet = 4)] = "foot";
  CubeGlobal.uType = uType;

  // Unit types for measured area Area
  const uAreaType: AreaUnitLookup = [];
  uAreaType[(uAreaType.sqmm = 0)] = "mm";
  uAreaType[(uAreaType.sqcm = 1)] = "cm";
  uAreaType[(uAreaType.sqm = 2)] = "m";
  uAreaType[(uAreaType.sqinch = 3)] = "inch";
  uAreaType[(uAreaType.sqfeet = 4)] = "foot";
  uAreaType[(uAreaType.acre = 5)] = "Acre";
  uAreaType[(uAreaType.hectare = 6)] = "hectare";
  CubeGlobal.uAreaType = uAreaType;

  // Unit types for measured area Area
  const uVolumeType: VolumeUnitLookup = [];
  uVolumeType[(uVolumeType.cubmm = 0)] = "mm";
  uVolumeType[(uVolumeType.cubcm = 1)] = "cm";
  uVolumeType[(uVolumeType.cubm = 2)] = "m";
  uVolumeType[(uVolumeType.cubinch = 3)] = "inch";
  uVolumeType[(uVolumeType.cubfeet = 4)] = "foot";
  CubeGlobal.uVolumeType = uVolumeType;

  CubeGlobal.currentLinearUnit = CubeGlobal.uType.m;
  CubeGlobal.currentAreaUnit = CubeGlobal.uAreaType.sqm;
  CubeGlobal.currentVolumeUnit = CubeGlobal.uVolumeType.cubm;
  CubeGlobal.measureTextPadding = 5;
  CubeGlobal.measureTextColor = new Color(0, 0, 0);
  CubeGlobal.measureTextFontSize = 12;
  CubeGlobal.measureTextFontWeight = 0;
  CubeGlobal.measureBoxFillColor = new Color(255, 255, 255);
  CubeGlobal.measureBoxFillOpacity = 1.0;
  CubeGlobal.measureBoxBorderRadius = 0;
  CubeGlobal.measureSegmentTextVisiblity = true;
  CubeGlobal.measureSegmentTextBox = true;
  CubeGlobal.measureSegmentTextPadding = 5;
  CubeGlobal.measureSegmentTextColor = new Color(0, 0, 0);
  CubeGlobal.measureSegmentTextFontSize = 12;
  CubeGlobal.measureSegmentTextFontWeight = 0;
  CubeGlobal.measureSegmentTextBoxColor = new Color(255, 255, 255);
  CubeGlobal.measurementSegmentTextBoxOpacity = 1.0;
  CubeGlobal.measurementSegmentTextBoxRadius = 0;

  function ApplyTextBoxStyling(
    textBox: StyleableTextBox,
    padding: number,
    txtColor: Color,
    fontSize: number,
    fontWeight: number,
    boxColor: Color,
    boxOpacity: number,
    boxRadius: number,
  ) {
    // Text customization
    textBox.setPadding(padding);

    textBox._text?.setFillColor(txtColor);
    textBox._text?.setStrokeColor(txtColor);
    textBox._text?.setFontSize(fontSize);
    textBox._text?.setStrokeWidth(fontWeight);

    // Box customization
    textBox._box?.setFillColor(boxColor);
    textBox._box?.setFillOpacity(boxOpacity);
    textBox._box?.setBorderRadius(boxRadius);
  }

  window.CubeGlobal = window.CubeGlobal || {};
  CubeGlobal.ApplyMeasurementStyling = function (measurement: StyleableMarkup | null) {
    if (!measurement) return;

    if (measurement._textShape) {
      ApplyTextBoxStyling(
        measurement._textShape,
        CubeGlobal.measureTextPadding,
        CubeGlobal.measureTextColor,
        CubeGlobal.measureTextFontSize,
        CubeGlobal.measureTextFontWeight,
        CubeGlobal.measureBoxFillColor,
        CubeGlobal.measureBoxFillOpacity,
        CubeGlobal.measureBoxBorderRadius,
      );
    }

    measurement._texts?.setFillColor(CubeGlobal.measureSegmentTextColor);
    measurement._texts?.setStrokeColor(CubeGlobal.measureSegmentTextColor);
    measurement._texts?.setFontSize(CubeGlobal.measureSegmentTextFontSize);
    measurement._texts?.setStrokeWidth(CubeGlobal.measureSegmentTextFontWeight);

    if (measurement._textBoxes) {
      ApplyTextBoxStyling(
        measurement._textBoxes,
        CubeGlobal.measureSegmentTextPadding,
        CubeGlobal.measureSegmentTextColor,
        CubeGlobal.measureSegmentTextFontSize,
        CubeGlobal.measureSegmentTextFontWeight,
        CubeGlobal.measureSegmentBoxFillColor,
        CubeGlobal.measureSegmentBoxFillOpacity,
        CubeGlobal.measureSegmentBoxBorderRadius,
      );
    }
  };

  // Get unit multiplier based on unit type
  CubeGlobal.GetuMultiplier = function (unit: number) {
    switch (unit) {
      case CubeGlobal.uType.mm:
        return 1;
      case CubeGlobal.uType.cm:
        return 10;
      case CubeGlobal.uType.m:
        return 1000;
      case CubeGlobal.uType.inch:
        return 25.4;
      case CubeGlobal.uType.feet:
        return 12 * 25.4;
    }
  };

  CubeGlobal.GetLengthInDisplayUnits = function (linearmm: number, unit: number) {
    const unitDetails = getEstimateUnitDetails();
    if (!unitDetails || !unitDetails.linearDisplayUnit) return getLinearMarkupUnitsHelper(linearmm, unit);

    const { factor, selectedSymbol } = unitDetails.linearDisplayUnit;
    return `${(linearmm / factor).toFixed(2).toString()} ${selectedSymbol.name}`;
  };

  CubeGlobal.GetAreaUnitConveredString = function (areamm: number, unit: number) {
    const unitDetails = getEstimateUnitDetails();
    if (!unitDetails || !unitDetails.areaDisplayUnit) return getAreaMarkupUnitsHelper(areamm, unit);

    const { factor, selectedSymbol } = unitDetails.areaDisplayUnit;
    return `${(areamm / factor).toFixed(2).toString()} ${selectedSymbol.name}`;
  };

  CubeGlobal.GetVolumeUnitConveredString = function (volummm: number, unit: number) {
    const unitDetails = getEstimateUnitDetails();
    if (!unitDetails || !unitDetails.volumeDisplayUnit) return getVolumeMarkupUnitsHelper(volummm, unit);

    const { factor, selectedSymbol } = unitDetails.volumeDisplayUnit;
    return `${(volummm / factor).toFixed(2).toString()} ${selectedSymbol.name}`;
  };

  CubeGlobal.ProjectPointOnPlane = function (point3d: Point3, plane: Plane) {
    const distance = plane.distanceToPoint(point3d);
    const projDiffVector = plane.normal.copy().scale(distance);
    const projectedPoint = Point3.subtract(point3d, projDiffVector);
    return projectedPoint;
  };

  // Every index below is inside the `polygon.length` guards, so the assertions hold.
  CubeGlobal.isPointInsidePolygon2D = function (p: Point2, polygon: Point2[]) {
    let isInside = false;

    if (!polygon.length) return false;

    const min = new Point2(polygon[0]!.x, polygon[0]!.y);
    const max = new Point2(polygon[0]!.x, polygon[0]!.y);

    for (let i = 0; i < polygon.length; ++i) {
      if (polygon[i]!.x < min.x) min.x = polygon[i]!.x;
      if (polygon[i]!.y < min.y) min.y = polygon[i]!.y;
      if (polygon[i]!.x > max.x) max.x = polygon[i]!.x;
      if (polygon[i]!.y > max.y) max.y = polygon[i]!.y;
    }

    if (p.x < min.x || p.x > max.x || p.y < min.y || p.y > max.y) return false;

    let i = 0;
    let j = polygon.length - 1;
    for (; i < polygon.length; j = i++) {
      if (
        polygon[i]!.y > p.y != polygon[j]!.y > p.y &&
        p.x <
          ((polygon[j]!.x - polygon[i]!.x) * (p.y - polygon[i]!.y)) / (polygon[j]!.y - polygon[i]!.y) + polygon[i]!.x
      ) {
        isInside = !isInside;
      }
    }
    return isInside;
  };
  CubeGlobal.ComputeVectorToVectorRotationMatrix = function (p1: Point3, p2: Point3) {
    const EPSILON = 0.0000001;
    p1.normalize();
    p2.normalize();
    const p3 = Point3.cross(p1, p2);

    const dprod = Point3.dot(p1, p2);
    const l = p3.length();

    // Construct a perpendicular vector for anti-parallel case
    if (l > -EPSILON && l < EPSILON) {
      if (dprod < 0) {
        if (p1.x < -EPSILON || p1.x > EPSILON) {
          p3.x = p1.y;
          p3.y = -p1.x;
          p3.z = 0;
        } else if (p1.y < -EPSILON || p1.y > EPSILON) {
          p3.x = -p1.y;
          p3.y = p1.x;
          p3.z = 0;
        } else if (p1.z < -EPSILON || p1.z > EPSILON) {
          p3.x = -p1.z;
          p3.z = p1.x;
          p3.y = 0;
        } else {
          return new Matrix();
        }
      } else {
        return new Matrix();
      }
    }

    let ang = Math.atan2(l, dprod);
    ang *= 180 / Math.PI;

    return Matrix.createFromOffAxisRotation(p3, ang);
  };

  CubeGlobal.createCubeMesh = async function (viewer: IWebViewer, showFaces: boolean, offset?: Point3, scale?: number) {
    let length;
    if (scale != undefined) length = 0.5 * scale;
    else length = 0.5;

    const meshData = new MeshData();
    meshData.setFaceWinding(FaceWinding.Clockwise);

    const vertices = [
      // front
      -length,
      length,
      length,
      length,
      length,
      length,
      -length,
      -length,
      length,
      length,
      length,
      length,
      length,
      -length,
      length,
      -length,
      -length,
      length,
      // back
      length,
      length,
      -length,
      -length,
      length,
      -length,
      -length,
      -length,
      -length,
      length,
      length,
      -length,
      -length,
      -length,
      -length,
      length,
      -length,
      -length,
      // top
      -length,
      length,
      -length,
      length,
      length,
      -length,
      length,
      length,
      length,
      -length,
      length,
      -length,
      length,
      length,
      length,
      -length,
      length,
      length,
      // bottom
      -length,
      -length,
      -length,
      length,
      -length,
      length,
      length,
      -length,
      -length,
      -length,
      -length,
      -length,
      -length,
      -length,
      length,
      length,
      -length,
      length,
      // left
      -length,
      length,
      -length,
      -length,
      length,
      length,
      -length,
      -length,
      -length,
      -length,
      length,
      length,
      -length,
      -length,
      length,
      -length,
      -length,
      -length,
      // right
      length,
      length,
      length,
      length,
      length,
      -length,
      length,
      -length,
      -length,
      length,
      length,
      length,
      length,
      -length,
      -length,
      length,
      -length,
      length,
    ];
    if (offset != undefined) {
      for (let i = 0; i < vertices.length; i += 3) {
        vertices[i]! += offset.x;
        vertices[i + 1]! += offset.y;
        vertices[i + 2]! += offset.z;
      }
    }
    const normals = [
      // front
      0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1,
      // back
      0, 0, -1, 0, 0, -1, 0, 0, -1, 0, 0, -1, 0, 0, -1, 0, 0, -1,
      // top
      0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0,
      // bottom
      0, -1, 0, 0, -1, 0, 0, -1, 0, 0, -1, 0, 0, -1, 0, 0, -1, 0,
      // left
      -1, 0, 0, -1, 0, 0, -1, 0, 0, -1, 0, 0, -1, 0, 0, -1, 0, 0,
      // right
      1, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0,
    ];

    const polylines = [
      [
        -length,
        length,
        length,
        length,
        length,
        length,
        length,
        -length,
        length,
        -length,
        -length,
        length,
        -length,
        length,
        length,
      ],
      [
        length,
        length,
        length,
        length,
        length,
        -length,
        length,
        -length,
        -length,
        length,
        -length,
        length,
        length,
        length,
        length,
      ],
      [
        -length,
        length,
        -length,
        length,
        length,
        -length,
        length,
        -length,
        -length,
        -length,
        -length,
        -length,
        -length,
        length,
        -length,
      ],
      [
        -length,
        length,
        length,
        -length,
        length,
        -length,
        -length,
        -length,
        -length,
        -length,
        -length,
        length,
        -length,
        length,
        length,
      ],
    ];
    if (showFaces) meshData.addFaces(vertices, normals);
    else {
      for (const polyline of polylines) meshData.addPolyline(polyline);
    }
    return await viewer.model.createMesh(meshData);
  };

  CubeGlobal.createLineMesh = async function (viewer: IWebViewer, start: Point3, end: Point3) {
    const meshData = new MeshData();
    meshData.setFaceWinding(FaceWinding.Clockwise);
    meshData.addPolyline([start.x, start.y, start.z, end.x, end.y, end.z]);

    return await viewer.model.createMesh(meshData);
  };

  CubeGlobal.hexToRgb = function (hex: string) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? new Color(parseInt(result[1]!, 16), parseInt(result[2]!, 16), parseInt(result[3]!, 16)) : null;
  };

  CubeGlobal.constructPointArray = function (pointJasonArray: unknown[]) {
    for (var a: Point3[] = [], c = 0; c < pointJasonArray.length; c++) {
      var e = Point3.fromJson(pointJasonArray[c]);
      a.push(e);
    }
    return a;
  };
};

interface EstimateDisplayUnit {
  factor: number;
  selectedSymbol: { name: string };
}

/** A display unit is only usable by the markup formatters when it carries both a factor and a
 *  symbol name; anything less is reported as absent so the caller falls back to the raw helper. */
function toDisplayUnit(unit: EstimateData["volumeDisplayUnit"]): EstimateDisplayUnit | undefined {
  const symbolName = unit?.selectedSymbol?.name;
  if (unit === undefined || typeof unit.factor !== "number" || symbolName === undefined) return undefined;
  return { factor: unit.factor, selectedSymbol: { name: symbolName } };
}

function getEstimateUnitDetails() {
  const state = store.getState();
  const { estimateData } = state;

  if (estimateData._id) {
    return {
      linearDisplayUnit: toDisplayUnit(estimateData.linearDisplayUnit),
      areaDisplayUnit: toDisplayUnit(estimateData.areaDisplayUnit),
      volumeDisplayUnit: toDisplayUnit(estimateData.volumeDisplayUnit),
      countDisplayUnit: toDisplayUnit(estimateData.countDisplayUnit),
    };
  }

  return null;
}

function getLinearMarkupUnitsHelper(linearmm: number, unit: number) {
  switch (unit) {
    case CubeGlobal.uType.mm:
      return `${linearmm.toFixed(2).toString()} mm `;
    case CubeGlobal.uType.cm:
      return `${(0.1 * linearmm).toFixed(2).toString()} cm `;
    case CubeGlobal.uType.m:
      return `${(0.001 * linearmm).toFixed(2).toString()} m `;
    case CubeGlobal.uType.inch:
      return `${(0.0393701 * linearmm).toFixed(2).toString()} in `;
    case CubeGlobal.uType.feet:
      return `${(0.00328084 * linearmm).toFixed(2).toString()} ft `;
  }
}

function getAreaMarkupUnitsHelper(areamm: number, unit: number) {
  switch (unit) {
    case CubeGlobal.uAreaType.sqmm:
      return `${areamm.toFixed(2).toString()} mm\u00B2 `;
    case CubeGlobal.uAreaType.sqcm:
      return `${(0.01 * areamm).toFixed(2).toString()} cm\u00B2 `;
    case CubeGlobal.uAreaType.sqm:
      return `${(0.000001 * areamm).toFixed(2).toString()} m\u00B2 `;
    case CubeGlobal.uAreaType.sqinch:
      return `${(0.00155 * areamm).toFixed(2).toString()} in\u00B2 `;
    case CubeGlobal.uAreaType.sqfeet:
      return `${(0.00001076 * areamm).toFixed(2).toString()} ft\u00B2 `;
    case CubeGlobal.uAreaType.acre:
      return `${(0.0000000002471 * areamm).toFixed(2).toString()} acres `;
    case CubeGlobal.uAreaType.hectare:
      return `${(0.0000000001 * areamm).toFixed(2).toString()} hectare `;
  }
}

function getVolumeMarkupUnitsHelper(volummm: number, unit: number) {
  switch (unit) {
    case CubeGlobal.uAreaType.sqmm:
      return `${volummm.toFixed(2).toString()} mm\u00B3 `;
    case CubeGlobal.uAreaType.sqcm:
      return `${(0.001 * volummm).toFixed(2).toString()} cm\u00B3 `;
    case CubeGlobal.uAreaType.sqm:
      return `${(0.000000001 * volummm).toFixed(2).toString()} m\u00B3 `;
    case CubeGlobal.uAreaType.sqinch:
      return `${(0.0000610237 * volummm).toFixed(2).toString()} in\u00B3 `;
    case CubeGlobal.uAreaType.sqfeet:
      return `${(0.0000000353147 * volummm).toFixed(2).toString()} ft\u00B3 `;
  }
}

export function getAllLeafNodesRecur(node: NodeId, childs: NodeId[]) {
  const c: NodeId[] = CubeGlobal.hwv.model.getNodeChildren(node);
  if (c.length == 0) {
    childs.push(node);
    return;
  }
  c.forEach((i: NodeId) => getAllLeafNodesRecur(i, childs));
}
